'use client'

import { Mail, Phone } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { Link } from '@/i18n/navigation'
import { SITE_CONFIG, COMPANY_INFO } from '@/lib/constants'
import { flagshipAccentAt, FLAGSHIP_ACCENT_VAR } from '@/lib/flagship-colors'
import { LegalPage, GlassSection, SubSection, InfoBox } from './legal/LegalPage'

const accent = (i: number) => FLAGSHIP_ACCENT_VAR[flagshipAccentAt(i)]

export default function DatenschutzPageClient() {
  const t = useTranslations('legal.datenschutz')
  const howWeCollectParagraphs = t.raw('section1.collection.howWeCollect.paragraphs') as string[]
  const privacyParagraphs = t.raw('section2.privacy.paragraphs') as string[]
  const cookiesParagraphs = t.raw('section3.cookies.paragraphs') as string[]
  const serverLogItems = t.raw('section3.serverLogs.items') as string[]
  const contactFormParagraphs = t.raw('section3.contactForm.paragraphs') as string[]
  const cdnParagraphs = t.raw('section3.cdn.paragraphs') as string[]
  const rightsItems = t.raw('section4.items') as string[]

  return (
    <LegalPage title={t('title')} subtitle={t('subtitle')}>
      {/* Section 1: Overview */}
      <GlassSection title={t('section1.heading')} accent={accent(0)}>
        <SubSection title={t('section1.general.heading')}>
          <p>{t('section1.general.paragraph')}</p>
        </SubSection>
        <SubSection title={t('section1.collection.heading')}>
          <SubSection title={t('section1.collection.responsible.heading')} level={4}>
            <p>{t('section1.collection.responsible.paragraph')}</p>
          </SubSection>
          <SubSection title={t('section1.collection.howWeCollect.heading')} level={4}>
            {howWeCollectParagraphs.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </SubSection>
          <SubSection title={t('section1.collection.whatFor.heading')} level={4}>
            <p>{t('section1.collection.whatFor.paragraph')}</p>
          </SubSection>
          <SubSection title={t('section1.collection.yourRights.heading')} level={4}>
            <p>{t('section1.collection.yourRights.paragraph')}</p>
          </SubSection>
        </SubSection>
      </GlassSection>

      {/* Section 2: General information */}
      <GlassSection title={t('section2.heading')} accent={accent(1)}>
        <SubSection title={t('section2.privacy.heading')}>
          {privacyParagraphs.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
        </SubSection>
        <SubSection title={t('section2.responsibleParty.heading')}>
          <p>{t('section2.responsibleParty.intro')}</p>
          <InfoBox>
            <p className="font-semibold text-[var(--flagship-text)]">{COMPANY_INFO.legalName}</p>
            <p>{COMPANY_INFO.ownerName}</p>
            <p>{COMPANY_INFO.address.street}</p>
            <p>
              {COMPANY_INFO.address.postalCode} {COMPANY_INFO.address.city}
            </p>
            <p className="mt-2">
              {t('section2.responsibleParty.phoneLabel')} {SITE_CONFIG.phone}
            </p>
            <p>
              {t('section2.responsibleParty.emailLabel')} {SITE_CONFIG.email}
            </p>
          </InfoBox>
        </SubSection>
      </GlassSection>

      {/* Section 3: Data collection */}
      <GlassSection title={t('section3.heading')} accent={accent(2)}>
        <SubSection title={t('section3.cookies.heading')}>
          {cookiesParagraphs.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
        </SubSection>
        <SubSection title={t('section3.serverLogs.heading')}>
          <p>{t('section3.serverLogs.intro')}</p>
          <ul className="list-inside list-disc space-y-1">
            {serverLogItems.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <p>{t('section3.serverLogs.outro')}</p>
        </SubSection>
        <SubSection title={t('section3.contactForm.heading')}>
          {contactFormParagraphs.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
        </SubSection>
        <SubSection title={t('section3.cdn.heading')}>
          {cdnParagraphs.map((paragraph) => (
            <p key={paragraph} className="break-words">
              {paragraph}
            </p>
          ))}
        </SubSection>
      </GlassSection>

      {/* Section 4: Your rights */}
      <GlassSection title={t('section4.heading')} accent={accent(0)}>
        <p>{t('section4.intro')}</p>
        <ul className="list-inside list-disc space-y-2">
          {rightsItems.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </GlassSection>

      {/* Section 5: Contact */}
      <GlassSection title={t('section5.heading')} accent={accent(1)}>
        <p>{t('section5.intro')}</p>
        <InfoBox>
          <p className="font-semibold text-[var(--flagship-text)]">{COMPANY_INFO.legalName}</p>
          <p className="mt-2 flex items-center gap-2">
            <Mail className="h-4 w-4 shrink-0 text-[var(--flagship-cyan)]" />
            <a href={`mailto:${SITE_CONFIG.email}`}>{SITE_CONFIG.email}</a>
          </p>
          <p className="flex items-center gap-2">
            <Phone className="h-4 w-4 shrink-0 text-[var(--flagship-cyan)]" />
            <a href={`tel:${SITE_CONFIG.phone}`}>{SITE_CONFIG.phone}</a>
          </p>
        </InfoBox>
      </GlassSection>

      {/* GDPR services */}
      <GlassSection title={t('gdprBox.heading')} accent={accent(2)}>
        <p>
          {t.rich('gdprBox.paragraph1', {
            services: (chunks) => <Link href="/">{chunks}</Link>,
            website: (chunks) => <Link href="/">{chunks}</Link>,
          })}
        </p>
        <p>
          {t.rich('gdprBox.paragraph2', {
            contact: (chunks) => <Link href="/#contact">{chunks}</Link>,
            about: (chunks) => <Link href="/#about">{chunks}</Link>,
          })}
        </p>
      </GlassSection>

      <p className="mx-auto max-w-none! text-center text-sm text-[var(--flagship-muted)]">{t('lastUpdated')}</p>
    </LegalPage>
  )
}
